import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

class VideoHeroComponent extends Component {
  connectedCallback() {
    super.connectedCallback();

    if (prefersReducedMotion()) {
      this.#applyTransforms(0);
      return;
    }

    this.#rows = Array.from(this.querySelectorAll('[data-scroll-speed]'));

    if (this.#rows.length === 0) return;

    this.#mediaQuery = window.matchMedia('(max-width: 749px)');
    this.#intersectionObserver = new IntersectionObserver(this.#handleIntersection, {
      rootMargin: '200px 0px',
    });

    this.#intersectionObserver.observe(this);
    window.addEventListener('scroll', this.#handleScroll, { passive: true });
    window.addEventListener('resize', this.#handleResize);
    this.#mediaQuery.addEventListener('change', this.#handleResize);

    this.#updateTargetProgress();
    this.#currentProgress = this.#targetProgress;
    this.#applyTransforms(this.#currentProgress);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#intersectionObserver?.disconnect();
    window.removeEventListener('scroll', this.#handleScroll);
    window.removeEventListener('resize', this.#handleResize);
    this.#mediaQuery?.removeEventListener('change', this.#handleResize);

    if (this.#rafId) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = 0;
    }
  }

  /**
   * @type {HTMLElement[]}
   */
  #rows = [];

  /**
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * @type {MediaQueryList | null}
   */
  #mediaQuery = null;

  /**
   * @type {number}
   */
  #rafId = 0;

  /**
   * @type {boolean}
   */
  #isNearViewport = false;

  /**
   * @type {number}
   */
  #targetProgress = 0;

  /**
   * @type {number}
   */
  #currentProgress = 0;

  #handleIntersection = ([entry]) => {
    this.#isNearViewport = entry?.isIntersecting ?? false;

    if (this.#isNearViewport) {
      this.#updateTargetProgress();
      this.#requestTick();
    }
  };

  #handleScroll = () => {
    this.#updateTargetProgress();

    if (this.#isNearViewport) {
      this.#requestTick();
    }
  };

  #handleResize = () => {
    this.#updateTargetProgress();
    this.#requestTick();
  };

  #requestTick() {
    if (this.#rafId) return;

    this.#rafId = requestAnimationFrame(this.#tick);
  }

  #tick = () => {
    this.#rafId = 0;

    const nextProgress = this.#currentProgress + (this.#targetProgress - this.#currentProgress) * this.#scrubFactor;
    const isSettled = Math.abs(nextProgress - this.#targetProgress) < 0.001;

    this.#currentProgress = isSettled ? this.#targetProgress : nextProgress;
    this.#applyTransforms(this.#currentProgress);

    if (!isSettled) {
      this.#requestTick();
    }
  };

  get #scrubFactor() {
    const rawValue = Number(this.dataset.scrub ?? 12) / 100;

    return Math.min(Math.max(rawValue, 0.01), 1);
  }

  get #shiftAmount() {
    const shiftInViewportWidth = this.#mediaQuery?.matches
      ? Number(this.dataset.shiftMobile ?? this.dataset.shiftDesktop ?? 0)
      : Number(this.dataset.shiftDesktop ?? 0);

    return (shiftInViewportWidth / 100) * window.innerWidth;
  }

  #updateTargetProgress() {
    const rect = this.getBoundingClientRect();
    const scrollRange = Math.max(rect.height - window.innerHeight, 1);

    if (scrollRange <= 1) {
      this.#targetProgress = rect.top <= 0 ? 1 : 0;
      return;
    }

    const rawProgress = -rect.top / scrollRange;
    this.#targetProgress = Math.min(Math.max(rawProgress, 0), 1);
  }

  /**
   * @param {number} progress
   */
  #applyTransforms(progress) {
    const shiftAmount = this.#shiftAmount;

    for (const row of this.#rows) {
      const speed = Number(row.dataset.scrollSpeed ?? 0);
      row.style.setProperty('--video-hero-translate', `${shiftAmount * speed * progress}px`);
    }
  }
}

if (!customElements.get('video-hero-component')) {
  customElements.define('video-hero-component', VideoHeroComponent);
}
